import React, { useMemo, useCallback } from 'react';
import {
  ListSubheader,
  IconButton,
  Typography,
  Checkbox,
  Tooltip
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DateTime } from 'luxon';
import { FormattedMessage } from 'react-intl';
import { useMeContext } from 'modules/me';
import { SmartDiaryEntry } from '../type';
import { useSmartDiaryCheckboxIcons } from '../hooks/useSmartDiaryCheckboxIcons';
import { useGroupCheckState } from './hooks';

type SmartDiaryListItemHeaderType = {
  title: string;
  onExpandCollapse: (title: string) => void;
  expanded?: boolean;
  smartEntries: SmartDiaryEntry[];
};

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    justifyContent: 'space-between',
    backgroundColor: theme.palette.grey[200],
    alignItems: 'center',
    paddingLeft: theme.spacing(2),
    marginBottom: ({ expanded }: { expanded: boolean }) =>
      expanded ? theme.spacing(0) : theme.spacing(0.25),
    cursor: 'pointer',
    marginRight: theme.spacing(-1),
    zIndex: 2
  },
  icon: {
    margin: theme.spacing(0.25),
    padding: theme.spacing(0)
  },
  iconColor: {
    color: theme.palette.text.primary
  },
  title: {
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.secondary,
    paddingLeft: theme.spacing(0.5)
  },
  daySelectionWrapper: {
    display: 'flex',
    alignItems: 'center'
  },
  actionContainer: {
    display: 'flex'
  },
  duration: {
    alignSelf: 'center',
    color: theme.palette.text.secondary,
    paddingRight: theme.spacing(0.75)
  }
}));
const useCheckBoxStyles = makeStyles((theme) => ({
  root: {
    color: theme.palette.grey[400],
    padding: theme.spacing(0.875, 1),
    '&:hover': {
      color: theme.palette.grey[500]
    },
    '&.Mui-checked': {
      color: theme.palette.grey[700]
    },
    '&.MuiCheckbox-indeterminate': {
      color: theme.palette.grey[700]
    },
    '&.Mui-checked:hover': {
      color: theme.palette.grey[700]
    }
  }
}));
export const SmartDiaryListItemHeader: React.FC<SmartDiaryListItemHeaderType> = ({
  title,
  onExpandCollapse,
  expanded = false,
  smartEntries
}: SmartDiaryListItemHeaderType) => {
  const classes = useStyles({ expanded });
  const me = useMeContext();
  const luxonFormat =
    me?.locale?.dateTimeSettings?.shortDateFormat?.luxonPattern ||
    'MMM d, yyyy';

  const {
    checked,
    indeterminate,
    disable,
    onChange,
    totalDuration
  } = useGroupCheckState(smartEntries);
  const onClick = useCallback(() => {
    onExpandCollapse(title);
  }, [title, onExpandCollapse]);
  const checkBoxStyles = useCheckBoxStyles();

  const { collapseIcon, expandIcon } = useMemo(
    () => ({
      collapseIcon: (
        <Tooltip title={<FormattedMessage id="collapse" />}>
          <IconButton
            className={classes.icon}
            data-testid={`expand-less-icon-${title}`}
            size="large"
          >
            <ExpandLessIcon className={classes.iconColor} />
          </IconButton>
        </Tooltip>
      ),
      expandIcon: (
        <Tooltip title={<FormattedMessage id="expand" />}>
          <IconButton
            className={classes.icon}
            data-testid={`expand-more-icon-${title}`}
            size="large"
          >
            <ExpandMoreIcon className={classes.iconColor} />
          </IconButton>
        </Tooltip>
      )
    }),
    [classes.icon, classes.iconColor, title]
  );
  const inputProps = useMemo(
    () => ({
      inputProps: {
        'data-testid': `note-list-item-header-clickable_checkbox_${title}`
      } as any
    }),
    [title]
  );
  const iconProps = useSmartDiaryCheckboxIcons();
  const stopPropagation = useCallback((event) => event.stopPropagation(), []);

  return (
    <ListSubheader
      data-testid={`notes-list-item-header-${title}`}
      className={classes.container}
      onClick={onClick}
    >
      <div className={classes.daySelectionWrapper}>
        <Checkbox
          color="default"
          classes={checkBoxStyles}
          checked={checked}
          indeterminate={indeterminate}
          disabled={disable}
          onChange={onChange}
          data-testid={`note-list-item-header-checkbox-${title}`}
          onClick={stopPropagation}
          {...inputProps}
          {...iconProps}
        />
        <Typography
          data-testid="day-header"
          className={classes.title}
          variant="subtitle1"
        >
          {DateTime.fromISO(title).toFormat(luxonFormat)}
        </Typography>
      </div>
      <div className={classes.actionContainer}>
        <Typography
          variant="body2"
          className={classes.duration}
          data-testid={`note-list-item-header-duration-${title}`}
        >
          {totalDuration}
        </Typography>
        {expanded ? collapseIcon : expandIcon}
      </div>
    </ListSubheader>
  );
};

export default SmartDiaryListItemHeader;
